import React, { useState } from "react";
import { useUserApi } from "../../../services/UserApi";
import { useAppContext } from "../../../contexts/AppContext";
import CommentsDialog from "../../common/CommentsDialog";
import ChangeEmailDialog from "../changeEmail/ChangeEmailDialog";
import VerifyEmailAccessDialog from "../verifyEmailAccess/VerifyEmailAccessDialog";
import ResetPasswordDialog from "../resetPassword/ResetPasswordDialog";
import Button from "carbon-react/lib/components/button";
import SplitButton from "carbon-react/lib/components/split-button";
import DeleteUserDialog from "../deleteUser/DeleteUserDialog";
import { useHistory } from "react-router-dom";
import appRoutes from "../../Navigation/appRoutes";
import BypassEmailVerificationDialog from "../bypassEmailVerification/BypassEmailVerificationDialog";

const UserActionsButton = ({ user, onUpdateUser }) => {
  const { blockUser, unblockUser } = useUserApi();
  const { agentEntitlement, userResultSet, setUserResultSet } = useAppContext();
  const [commentsDialogState, setCommentsDialogState] = useState(null);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [changeEmailDialogOpen, setChangeEmailDialogOpen] = useState(false);
  const [
    verifyEmailAccessDialogOpen,
    setVerifyEmailAccessDialogOpen,
  ] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [
    bypassEmailVerificationDialogOpen,
    setBypassEmailVerificationDialogOpen,
  ] = useState(false);
  const history = useHistory();

  // TODO: RB - refactor this duplication
  const dialogStates = {
    blockUserState: {
      captureLanguage: true,
      title: `Block user`,
      userHeading: `${user.name} (${user.email})`,
      subTitle:
        "Blocking this user will disable the account, thereby preventing access to all associated services/products.",
      promptCaption: "Confirm",
      prompts: [
        "Before proceeding, please ensure that you have positively verified that the person requesting this action is the owner of the account.",
      ],
      buttonCaption: "Block user",
      performAction: async (params) => {
        const result = await blockUser(
          user.userId,
          params?.languageCode,
          params?.agentComments
        );
        if (!result.success)
          throw new Error(result.error?.description || "Failed to block user");
        onUpdateUser(result.data);
      },
      successToast: `User blocked '${user.email}'.`,
    },
    unblockUserState: {
      captureLanguage: true,
      title: `Unblock user`,
      userHeading: `${user.name} (${user.email})`,
      subTitle:
        "Unblocking this user will re-enable the account, thereby restoring access to all associated services/products.",
      promptCaption: "Confirm",
      prompts: [
        "Before proceeding, please ensure that you have positively verified that the person requesting this action is the owner of the account.",
      ],
      buttonCaption: "Unblock user",
      performAction: async (params) => {
        const result = await unblockUser(
          user.userId,
          params?.languageCode,
          params?.agentComments
        );
        if (!result.success)
          throw new Error(
            result.error?.description || "Failed to unblock user"
          );
        onUpdateUser(result.data);
      },
      successToast: `User unblocked '${user.email}'.`,
    },
  };

  const openCommentsDialog = (dialogState) => {
    setCommentsDialogState(dialogState);
    setCommentsDialogOpen(true);
  };

  const onCommentsDialogClose = () => {
    setCommentsDialogState(null);
    setCommentsDialogOpen(false);
  };

  const openDeleteUserDialog = () => {
    setDeleteUserDialogOpen(true);
  };

  const closeDeleteUserDialog = (wasDeleted = false) => {
    setDeleteUserDialogOpen(false);
    if (wasDeleted) {
      history.replace(appRoutes.users);
      removeUserFromCachedResultsetOnDelete();
    }
  };

  const removeUserFromCachedResultsetOnDelete = () => {
    try {
      if (!userResultSet?.users || userResultSet.users.length === 0) return;

      const deletedUserIdx = userResultSet.users.findIndex(
        (usr) => usr.userId === user.userId
      );

      if (deletedUserIdx < 0) return;

      userResultSet.users.splice(deletedUserIdx, 1);
      setUserResultSet({ ...userResultSet });
    } catch (error) {
      //ignore
    }
  };

  const splitButtonItem_Block_Unblock = (
    <>
      {user.blocked === true
        ? agentEntitlement &&
          agentEntitlement.users.canUnblock && (
            <Button
              id="manage-user-actions-unblock-button"
              onClick={() => openCommentsDialog(dialogStates.unblockUserState)}
              iconType={"unlocked"}
              className="buttonWhiteIcon"
            >
              Unblock user
            </Button>
          )
        : agentEntitlement &&
          agentEntitlement.users.canBlock && (
            <Button
              id="manage-user-actions-block-button"
              onClick={() => openCommentsDialog(dialogStates.blockUserState)}
              iconType={"locked"}
              className="buttonWhiteIcon"
            >
              Block user
            </Button>
          )}
    </>
  );

  const splitButtonItem_ConfirmEmailAccess = (
    <>
      {agentEntitlement && agentEntitlement.users.canVerifyEmailAccess && (
        <Button
          id="manage-user-actions-confirm-access-button"
          onClick={() => {
            setVerifyEmailAccessDialogOpen(true);
          }}
          iconType={"person_tick"}
          className="buttonWhiteIcon"
        >
          Confirm email access
        </Button>
      )}
    </>
  );

  const splitButtonItem_Delete = (
    <>
      {agentEntitlement && agentEntitlement.users.canDelete && (
        <Button
          id="manage-user-actions-delete-button"
          onClick={() => openDeleteUserDialog()}
          iconType={"bin"}
          className="buttonWhiteIcon"
        >
          Delete user
        </Button>
      )}
    </>
  );

  const splitButtonItem_ChangeEmail = (
    <>
      {agentEntitlement && agentEntitlement.users.canChangeEmail && (
        <Button
          id="manage-user-actions-change-email-button"
          onClick={() => setChangeEmailDialogOpen(true)}
          iconType={"email"}
          className="buttonWhiteIcon"
        >
          Change email address
        </Button>
      )}
    </>
  );

  const splitButtonItem_ResetPassword = (
    <>
      {agentEntitlement && agentEntitlement.users.canResetPassword && (
        <Button
          id="manage-user-actions-reset-password-button"
          onClick={() => setResetPasswordDialogOpen(true)}
          iconType={"key"}
          className="buttonWhiteIcon"
        >
          Reset password
        </Button>
      )}
    </>
  );

  const splitButtonItem_BypassEmailVerification = (
    <>
      {agentEntitlement && agentEntitlement.users.canBypassEmailVerification && (
        <Button
          id="manage-user-actions-bypass-email-verification-button"
          onClick={() => setBypassEmailVerificationDialogOpen(true)}
          iconType={"arrow"}
          className="buttonWhiteIcon"
        >
          Bypass email verification
        </Button>
      )}
    </>
  );

  return (
    <>
      <SplitButton
        id="manage-user-actions-button"
        as="primary"
        text="Actions"
        iconType="settings"
        autoFocus
      >
        {splitButtonItem_ConfirmEmailAccess}
        {splitButtonItem_ChangeEmail}
        {splitButtonItem_Block_Unblock}
        {splitButtonItem_ResetPassword}
        {splitButtonItem_Delete}
        {splitButtonItem_BypassEmailVerification}
      </SplitButton>
      <CommentsDialog
        dialogState={commentsDialogState}
        open={commentsDialogOpen}
        onClose={onCommentsDialogClose}
      />
      <ChangeEmailDialog
        open={changeEmailDialogOpen}
        user={user}
        onSave={onUpdateUser}
        onClose={() => {
          setChangeEmailDialogOpen(false);
        }}
      />
      <VerifyEmailAccessDialog
        open={verifyEmailAccessDialogOpen}
        onClose={() => {
          setVerifyEmailAccessDialogOpen(false);
        }}
        user={user}
      />
      <ResetPasswordDialog
        open={resetPasswordDialogOpen}
        user={user}
        onClose={() => {
          setResetPasswordDialogOpen(false);
        }}
      />
      <DeleteUserDialog
        open={deleteUserDialogOpen}
        user={user}
        onCancel={closeDeleteUserDialog}
        onDeleted={() => closeDeleteUserDialog(true)}
      />
      <BypassEmailVerificationDialog
        open={bypassEmailVerificationDialogOpen}
        user={user}
        onClose={() => setBypassEmailVerificationDialogOpen(false)}
      />
    </>
  );
};

export default UserActionsButton;
